<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";
import {
  UiText,
  UiButton,
  UiAccordion,
  UiDialog,
  useToaster,
} from "@shopexperts/shared";
import type { QuotePaymentStatus } from "@shopexperts/legacy-api";
import type { ProjectSide, RawQuoteRecord } from "@/v2Projects/types";
import {
  formatShortDate,
  getDeliveryWindowLabel,
} from "@/v2Projects/utils/display";
import QuoteDetailStrip from "@/v2Projects/components/QuoteDetailStrip.vue";
import QuoteStatusDialog from "@/v2Projects/components/QuoteStatusDialog.vue";
import type { UseMutationReturnType } from "@tanstack/vue-query";
import {
  canExpertCancelQuote,
  canExpertManageQuotePaymentStatus,
  formatQuotePaymentStatusLabel,
  formatQuoteStatusLabel,
  getQuotePaymentModeLabel,
  getQuotePaymentUrl,
  getQuotePaymentBadgeClass,
  getQuotePaymentIndicatorClass,
  getQuoteStatusBadgeClass,
  formatQuoteTotal,
  isInternalQuotePayment,
  isClientQuoteActionable,
  normalizeQuotePaymentStatus,
} from "@/v2Projects/utils/quotePresentation";
import { reportProjectUserError } from "@/v2Projects/userErrors";
import { quoteCopy } from "@/v2Projects/utils/quoteCopy";

type UpdateQuotePaymentStatusMutation = UseMutationReturnType<
  unknown,
  Error,
  { quoteId: number; actingOrgId: number; paymentStatus: QuotePaymentStatus },
  unknown
>;

const props = defineProps<{
  quotes: RawQuoteRecord[];
  side: ProjectSide;
  actingOrgId: number;
  isLoading: boolean;
  cancelQuote: UseMutationReturnType<unknown, Error, { quoteId: number; actingOrgId: number }, unknown>;
  rejectQuote: UseMutationReturnType<unknown, Error, { quoteId: number; actingOrgId: number }, unknown>;
  updateQuotePaymentStatus: UpdateQuotePaymentStatusMutation;
}>();

const emit = defineEmits<{
  createQuote: [];
}>();

const toaster = useToaster();
const route = useRoute();
const isStatusDialogOpen = ref(false);
const isPaymentDialogOpen = ref(false);
const pendingExternalPaymentUrl = ref<string | null>(null);
const activeQuoteForStatusDialog = ref<RawQuoteRecord | null>(null);

const sortedQuotes = computed(() =>
  [...props.quotes].sort(
    (a, b) =>
      new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
  ),
);

const focusedQuoteId = computed(() => {
  const queryQuote = Array.isArray(route.query.quote) ? route.query.quote[0] : route.query.quote;
  const hashQuote = route.hash.match(/^#quote-(\d+)$/)?.[1] ?? null;
  const rawQuoteId = queryQuote ?? hashQuote;
  if (typeof rawQuoteId !== "string") return null;

  const quoteId = Number(rawQuoteId);
  return Number.isFinite(quoteId) ? quoteId : null;
});

function hasHtml(input: string | null | undefined): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(input ?? "");
}

watch(isStatusDialogOpen, (isOpen) => {
  if (!isOpen) {
    activeQuoteForStatusDialog.value = null;
  }
});

watch(
  [focusedQuoteId, sortedQuotes],
  async ([quoteId]) => {
    if (quoteId === null) return;

    await nextTick();
    document.getElementById(`quote-${quoteId}`)?.scrollIntoView({
      block: "center",
      behavior: "smooth",
    });
  },
  { immediate: true },
);

async function handleCancel(quote: RawQuoteRecord) {
  if (!canExpertCancelQuote(quote)) {
    toaster.create({
      title: "Paid quotes cannot be cancelled.",
      type: "error",
    });
    return;
  }

  try {
    await props.cancelQuote.mutateAsync({
      quoteId: quote.id,
      actingOrgId: props.actingOrgId,
    });
    toaster.create({ title: "Quote cancelled", type: "success" });
  } catch (error) {
    const message = reportProjectUserError("cancelQuote", error, {
      side: props.side,
      extra: { quoteId: quote.id, actingOrgId: props.actingOrgId },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  }
}

async function handleReject(quoteId: number) {
  try {
    await props.rejectQuote.mutateAsync({
      quoteId,
      actingOrgId: props.actingOrgId,
    });
    toaster.create({ title: "Quote rejected", type: "success" });
  } catch (error) {
    const message = reportProjectUserError("rejectQuote", error, {
      side: props.side,
      extra: { quoteId, actingOrgId: props.actingOrgId },
    });
    toaster.create({ title: message.title, description: message.description, type: "error" });
  }
}

function openStatusDialog(quote: RawQuoteRecord) {
  activeQuoteForStatusDialog.value = quote;
  isStatusDialogOpen.value = true;
}

function getQuoteCardAmount(quote: RawQuoteRecord): string {
  return formatQuoteTotal(quote);
}

function getQuoteCardPaymentUrl(quote: RawQuoteRecord): string | null {
  return getQuotePaymentUrl(quote);
}

function openPaymentLink(quote: RawQuoteRecord) {
  const paymentUrl = getQuoteCardPaymentUrl(quote);
  if (!paymentUrl) return;

  if (isInternalQuotePayment(quote.payment_mode)) {
    window.open(paymentUrl, "_blank", "noopener,noreferrer");
    return;
  }

  pendingExternalPaymentUrl.value = paymentUrl;
  isPaymentDialogOpen.value = true;
}

function confirmExternalPayment() {
  if (!pendingExternalPaymentUrl.value) return;
  window.open(pendingExternalPaymentUrl.value, "_blank", "noopener,noreferrer");
  isPaymentDialogOpen.value = false;
  pendingExternalPaymentUrl.value = null;
}
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col pt-4">
    <div v-if="isLoading" class="flex flex-1 items-center justify-center py-12">
      <div class="w-6 h-6 border-2 border-gray-800 border-t-transparent rounded-full animate-spin" />
    </div>

    <div
      v-else-if="sortedQuotes.length === 0"
      class="flex flex-1 flex-col items-center justify-center gap-3 py-20"
    >
      <div class="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
        </svg>
      </div>
      <UiText as="p" variant="p-small" class="text-gray-500">
        No quotes yet
      </UiText>
      <UiButton
        v-if="side === 'expert'"
        variant="primary"
        label="Offer Quote"
        @click="emit('createQuote')"
      />
    </div>

    <div v-else class="space-y-3">
      <div
        v-for="quote in sortedQuotes"
        :key="quote.id"
        :id="`quote-${quote.id}`"
        class="rounded-xl border border-gray-200 bg-white"
        :class="focusedQuoteId === quote.id ? 'ring-2 ring-se-highlight/60' : ''"
      >
        <!-- Card header: badges + amount -->
        <div class="flex items-start justify-between gap-4 px-5 pt-4 pb-3">
          <div class="flex flex-wrap items-center gap-1.5">
            <span
              class="inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-semibold capitalize"
              :class="getQuoteStatusBadgeClass(quote.status)"
            >
              {{ formatQuoteStatusLabel(quote.status) }}
            </span>
            <span
              v-if="quote.payment_status"
              class="inline-flex items-center rounded-sm border px-2 py-0.5 text-[11px] font-semibold capitalize"
              :class="getQuotePaymentBadgeClass(quote.payment_status)"
            >
              {{ formatQuotePaymentStatusLabel(quote.payment_status) }}
            </span>
            <span
              class="inline-flex items-center rounded-sm border border-teal-200 bg-teal-50 px-2 py-0.5 text-[11px] font-semibold text-teal-700"
              :class="isInternalQuotePayment(quote.payment_mode) ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-teal-200 bg-teal-50 text-teal-700'"
            >
              {{ getQuotePaymentModeLabel(quote.payment_mode) }}
            </span>
          </div>

          <UiText as="span" variant="p" class="font-semibold text-gray-900 tabular-nums shrink-0">
            {{ getQuoteCardAmount(quote) }}
          </UiText>
        </div>

        <!-- Quote detail strip -->
        <div class="px-5 pb-4">
          <QuoteDetailStrip :quote="quote" />

          <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
            <span v-if="quote.created_at">Created {{ formatShortDate(quote.created_at) }}</span>
            <span v-if="quote.status_changed_at">&middot; Updated {{ formatShortDate(quote.status_changed_at) }}</span>
            <template v-if="quote.delivery_after_days != null">
              <span>&middot; {{ getDeliveryWindowLabel(quote.delivery_after_days, quote.includes_weekends ?? false) }}</span>
            </template>
            <span v-if="getQuoteCardPaymentUrl(quote)">
              &middot; {{ isInternalQuotePayment(quote.payment_mode) ? 'Shopexperts payment link ready' : 'External payment link added' }}
            </span>
          </div>

          <div
            v-if="quote.hours_bank_expiration_terms"
            class="mt-3 rounded-lg bg-gray-50 px-4 py-3"
          >
            <UiText as="p" variant="caption-normal" class="mb-1 uppercase text-gray-500">
              {{ quoteCopy.hoursBankExpirationTermsDisplayLabel }}
            </UiText>
            <p class="whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
              {{ quote.hours_bank_expiration_terms }}
            </p>
          </div>
        </div>

        <!-- Scope of work (collapsible via UiAccordion) -->
        <div
          v-if="quote.scope"
          class="border-t border-gray-100 px-5 py-3"
        >
          <UiAccordion>
            <template #trigger>
              <UiText as="span" variant="p-small" class="font-semibold text-gray-700">
                Scope of work
              </UiText>
            </template>
            <div class="pt-3">
              <div
                class="rounded-lg bg-gray-50 px-4 py-3 prose prose-sm max-w-none text-gray-700 [&_p]:my-1 [&_ul]:my-2 [&_ol]:my-2"
              >
                <div v-if="hasHtml(quote.scope)" v-html="quote.scope" />
                <p v-else class="whitespace-pre-wrap">{{ quote.scope }}</p>
              </div>
            </div>
          </UiAccordion>
        </div>

        <!-- Actions -->
        <div
          v-if="
            side === 'expert' ||
            (side === 'client' && quote.status === 'offered') ||
            getQuoteCardPaymentUrl(quote)
          "
          class="flex flex-wrap items-center gap-2 border-t border-gray-100 px-5 py-3"
        >
          <template v-if="side === 'expert'">
            <UiButton
              v-if="canExpertManageQuotePaymentStatus(quote)"
              variant="outline"
              class="min-w-[198px]"
              justify="between"
              @click="openStatusDialog(quote)"
            >
              <template #label>
                <span class="flex items-center gap-2">
                  <span
                    class="h-2.5 w-2.5 rounded-full"
                    :class="getQuotePaymentIndicatorClass(normalizeQuotePaymentStatus(quote.payment_status))"
                  />
                  <span class="text-sm font-semibold text-gray-800">Payment Status</span>
                </span>
              </template>
              <template #after_label>
                <span class="text-[11px] font-medium text-gray-500">
                  {{ formatQuotePaymentStatusLabel(normalizeQuotePaymentStatus(quote.payment_status)) }}
                </span>
              </template>
            </UiButton>
            <UiButton
              v-if="canExpertCancelQuote(quote)"
              variant="danger"
              label="Cancel Quote"
              @click="handleCancel(quote)"
            />
          </template>

          <template v-if="side === 'expert' && quote.status === 'offered' && quote.payment_status === 'paid'">
            <span class="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
              Payment received
            </span>
          </template>

          <template v-if="side === 'client' && isClientQuoteActionable(quote)">
            <UiButton
              variant="ghost"
              label="Reject"
              @click="handleReject(quote.id)"
            />
            <UiButton
              v-if="getQuoteCardPaymentUrl(quote) && isInternalQuotePayment(quote.payment_mode)"
              variant="primary"
              label="Pay Now"
              show-arrow
              :href="getQuoteCardPaymentUrl(quote) ?? undefined"
              target="_blank"
              rel="noopener noreferrer"
            />
            <UiButton
              v-else-if="getQuoteCardPaymentUrl(quote)"
              variant="primary"
              label="Pay Now"
              show-arrow
              @click="openPaymentLink(quote)"
            />
          </template>

          <a
            v-if="getQuoteCardPaymentUrl(quote) && isInternalQuotePayment(quote.payment_mode) && !(side === 'client' && quote.status === 'offered')"
            :href="getQuoteCardPaymentUrl(quote) ?? undefined"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-h5 text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M6 3h7v7M13 3L6 10" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Shopexperts payment
          </a>

          <button
            v-else-if="getQuoteCardPaymentUrl(quote) && !(side === 'client' && quote.status === 'offered')"
            type="button"
            @click="openPaymentLink(quote)"
            class="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-h5 text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
          >
            <svg class="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M6 3h7v7M13 3L6 10" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Payment link
          </button>
        </div>
      </div>
    </div>

    <QuoteStatusDialog
      v-if="activeQuoteForStatusDialog"
      v-model="isStatusDialogOpen"
      :quote="activeQuoteForStatusDialog"
      :acting-org-id="actingOrgId"
      :update-quote-payment-status="updateQuotePaymentStatus"
    />

    <UiDialog
      v-model="isPaymentDialogOpen"
      title="Open external payment link?"
      text="This payment link is outside Shopexperts and was provided directly by the expert. Continue only if you trust the destination."
      primary-action-label="Continue to Payment"
      secondary-action-label="Cancel"
      @primary-action="confirmExternalPayment"
      @secondary-action="isPaymentDialogOpen = false"
    />
  </div>
</template>
